import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Dropdown, Form, InputGroup } from "react-bootstrap";
import { AiOutlineClose } from "react-icons/ai";
import {
    BiCheck, BiSearch, BiSquareRounded,
} from "react-icons/bi";
import { GrFormFilter } from "react-icons/gr";

export interface FilterPropGroup {
    label: string | ReactElement;
    options: FilterPropGroupItem[]
}

export interface FilterPropGroupItem {
    value: string;
    text?: string;
    label: string | ReactElement;
}

export interface FilterPropGroupDivider {
    isDivider: true;
}

export type FilterPropCategory = FilterPropGroupItem | FilterPropGroupDivider | FilterPropGroup;

interface FilterProps<TIsMulti = true> {
    categories?: FilterPropCategory[];
    selectedCategories?: TIsMulti extends true ? Set<string> : string;
    onSelected?: (ids: (TIsMulti extends true ? Set<string> : string)) => void;
    search?: string;
    onSetSearch?: (value: string) => void;
    onSearch?: (value: string) => Promise<FilterPropCategory[]>;
    isMulti?: TIsMulti;
    className?: string;
    toggleText?: string | ReactElement;
    toggleClassName?: string;
    canSelect?: boolean;
    inputClassName?: string;
    searchMinimum?: number;
}

function isDivider(item: FilterPropCategory): item is FilterPropGroupDivider {
    return Object.hasOwn(item, "isDivider");
}

function isGroup(item: FilterPropCategory): item is FilterPropGroup {
    return Object.hasOwn(item, "options");
}

const MAX_SEARCH = 10;
export function Filters<TIsMulti extends boolean = true>({
    categories: categoriesList, selectedCategories, onSelected, search, onSetSearch,
    isMulti, className, toggleText, toggleClassName,
    canSelect, inputClassName, searchMinimum, onSearch,
}: FilterProps<TIsMulti>): ReactElement | null {
    const [listSearch, setListSearch] = useState("");
    const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout>>();

    const [categories, setCategories] = useState<FilterPropCategory[]>(categoriesList ?? []);

    useEffect(() => {
        if (categoriesList) {
            setCategories(categoriesList);
        }
    }, [categoriesList]);

    useEffect(() => {
        if (!onSearch) {
            return;
        }

        clearTimeout(searchTimer);
        setSearchTimer(setTimeout(async () => {
            const results = await onSearch(listSearch);
            setCategories(results);
        }, 500));
    }, [listSearch]);

    if (categories?.isEmpty && !onSetSearch) {
        return null;
    }

    const categoriesFlat = categories?.map(row => (isGroup(row) ? row.options : row)).flat();
    let results = categories ?? [];

    if (listSearch) {
        results = results?.mapFilter(row => {
            if (isDivider(row)) {
                return row;
            } if (isGroup(row)) {
                return {
                    label: row.label,
                    options: row.options.filter(child => (child.text ?? child.label as string)
                        .toLowerCase?.()
                        .includes(listSearch.toLowerCase())),
                };
            } if ((row.label as string).toLowerCase?.().includes(listSearch.toLowerCase())) {
                return row;
            }
            return undefined;
        });
    }
    const canSearch = onSearch || (searchMinimum && categoriesFlat && categoriesFlat?.length > searchMinimum);
    const hasMore = (canSearch && results?.length > MAX_SEARCH) || (searchMinimum && categoriesFlat.length > searchMinimum);
    if (canSearch) {
        results = results?.limit(MAX_SEARCH);
    }
    const hasSelected = selectedCategories instanceof Set && selectedCategories?.size > 0;

    const renderItem = (item: FilterPropGroupItem) => (
        <Dropdown.Item
            key={item.value}
            eventKey={item.value}
            onClick={() => {
                if (isMulti) {
                    const seleted = selectedCategories as Set<string>;
                    if (seleted.has(item.value)) {
                        seleted.delete(item.value);
                    } else {
                        seleted.add(item.value);
                    }
                    const handler = onSelected as ((val: Set<string>) => void);
                    handler?.(new Set(Array.from(seleted)
                        .union(categoriesFlat?.mapFilter(
                            row => (!isDivider(row) ? row.value : undefined),
                        ) ?? [])));
                } else {
                    (onSelected as ((val: string) => void))?.(item.value);
                }
            }}
        >
            {canSelect && (
                ((selectedCategories instanceof Set)
            && selectedCategories.has(item.value)) || selectedCategories === item.value
                    ? <BiCheck className="text-blue-dark" />
                    : <BiSquareRounded className="text-blue-dark" />
            )}

            {" "}
            {item.label}
        </Dropdown.Item>
    );

    const selectedSet = canSelect && (selectedCategories instanceof Set) ? selectedCategories : new Set(selectedCategories);

    return (
        <div className={`d-flex align-items-center ${className}`}>
            {categories?.isNotEmpty && (
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                        variant=""
                        className={toggleText ? toggleClassName : "menu-small p-1"}
                    >
                        {toggleText ? (
                            <>
                                {toggleText}
                                {" "}
                                {selectedCategories instanceof Set
                                && selectedCategories?.size > 0
                                && `${selectedCategories.size} selected`}
                            </>
                        ) : <GrFormFilter />}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className={canSearch ? "pt-0" : undefined}>
                        {canSearch && (
                            <InputGroup className="border">
                                <InputGroup.Text id="search-categories" className="bg-transparent text-light border-0 pe-1">
                                    <BiSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="search"
                                    placeholder="Search..."
                                    className="mt-0 no-border p-2 placeholder-light"
                                    value={listSearch}
                                    aria-label="Search"
                                    aria-describedby="search-categories"
                                    onChange={event => setListSearch(event.target.value)}
                                />
                            </InputGroup>
                        )}
                        {((results.isNotEmpty && hasSelected && isMulti) || hasMore) && (
                            <div className="text-light mx-auto fst-italic w-100 text-center
                                            mt-0 px-2 py-1 w-100 border-bottom d-flex gap-2 justify-content-between"
                            >

                                {(results.isNotEmpty && hasSelected && isMulti) ? (
                                    <button
                                        type="button"
                                        className="btn btn-none p-0 text-light"
                                        onClick={() => {
                                            const handler = onSelected as ((val: Set<string>) => void);
                                            handler(new Set());
                                        }}
                                    >
                                        <BiCheck className="text-light" />
                                        {" "}
                                        {selectedCategories.size}
                                        {" "}
                                        <AiOutlineClose className="text-light" />
                                    </button>
                                ) : <div />}
                                {hasMore && (
                                    <div>
                                        {results.sum(row => (isGroup(row)
                                            ? Math.min(row.options.length, searchMinimum ?? 0)
                                            : 1))}
                                        {" of "}
                                        {categoriesFlat?.length}
                                        {" "}
                                        results.

                                    </div>
                                )}
                            </div>

                        )}

                        {listSearch && results.isEmpty && (
                            <div className="text-light mx-auto fst-italic w-100 text-center mt-2">
                                No results found.
                            </div>
                        )}

                        {canSearch && results?.mapFilter(item => {
                            if (isDivider(item)) {
                                return <Dropdown.Divider />;
                            } if (isGroup(item)) {
                                const children = item.options.filter(child => selectedSet.has(child.value));
                                return children.isNotEmpty ? (
                                    <div key={item.label as string}>
                                        <div className="text-gray-600 mx-2 mt-2">{item.label}</div>
                                        {children.limit(searchMinimum ?? -1).map(renderItem)}
                                    </div>
                                ) : undefined;
                            }
                            return selectedSet.has(item.value) ? renderItem(item) : undefined;
                        })}

                        {results?.mapFilter(item => {
                            if (isDivider(item)) {
                                return <Dropdown.Divider />;
                            } if (isGroup(item)) {
                                let children = item.options;
                                if (canSearch) {
                                    children = children.filter(child => !selectedSet.has(child.value));
                                }
                                return children.isNotEmpty ? (
                                    <div key={item.label as string}>
                                        <div className="text-gray-600 mx-2 mt-2">{item.label}</div>
                                        {children.limit(searchMinimum ?? -1).map(renderItem)}
                                    </div>
                                ) : undefined;
                            }
                            return (!canSearch || !selectedSet.has(item.value)) ? renderItem(item) : undefined;
                        })}

                    </Dropdown.Menu>
                </Dropdown>
            )}
            {onSetSearch && (
                <input
                    className={`no-border ms-2 ${inputClassName}`}
                    type="search"
                    placeholder="Search..."
                    value={search}
                    onChange={evt => onSetSearch(evt.target.value)}
                />
            )}

        </div>
    );
}

Filters.defaultProps = {
    categories: [],
    selectedCategories: undefined,
    onSelected: undefined,
    isMulti: true,
    onSetSearch: undefined,
    search: "",
    className: "mb-1",
    toggleText: undefined,
    toggleClassName: "",
    canSelect: true,
    inputClassName: "",
    searchMinimum: false,
    onSearch: undefined,
};
